import { EndpointHint, TIER_LABELS, Skeleton } from '../../core/index.js'
import { useDraftSchedule, useShiftPreferences, useWorkerScheduleResult } from '../hooks.js'
import type { DraftSchedule, ShiftPreferences } from '../types.js'
import { describePeriod, summarizePreferences } from './preferencesText.js'
import { describeDraft } from './DraftScheduleView.js'
import { LoadFailure } from '../../core/index.js'

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** One worker's preferences for a month as a list of lines, or that none were submitted. Shared by the roster row's tab, the admin's worker detail and the simulator. */
/** "20 shifts · 160 hrs · submitted Oct 16, 9:04 AM", or that the draft is behind the preferences, or that none is built. */
export function describeDraftStanding(draft: DraftSchedule | null, isStale: boolean): string {
  if (!draft) {
    return 'No draft built yet.'
  }
  const state = draft.status === 'accepted' && draft.acceptedAt ? `submitted ${formatWhen(draft.acceptedAt)}` : `built ${formatWhen(draft.builtAt)}, not yet submitted`
  const flags = draft.flags.includes('under_target') ? ' · under target' : ''
  return `${describeDraft(draft)} · ${state}${flags}${isStale ? ' · out of date, build again' : ''}`
}

export function ShiftPreferencesSummary({ preferences, period, draft, isStale = false }: { preferences: ShiftPreferences | null; period: string; draft?: DraftSchedule | null; isStale?: boolean }) {
  if (!preferences) {
    return <p className="text-[11px] text-ink-mid">Nothing submitted for {describePeriod(period)} yet.</p>
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
      {summarizePreferences(preferences).map((line) => (
        <div key={line.label} className="contents">
          <dt className="font-bold text-ink-mid">{line.label}</dt>
          <dd className="text-ink">{line.value}</dd>
        </div>
      ))}
      <dt className="font-bold text-ink-mid">Submitted</dt>
      <dd className="text-ink-mid">
        {formatWhen(preferences.submittedAt)}
        {preferences.updatedAt !== preferences.submittedAt && <> · updated {formatWhen(preferences.updatedAt)}</>}
      </dd>
      {draft !== undefined && (
        <>
          <dt className="font-bold text-ink-mid">Draft</dt>
          <dd className={draft?.status === 'accepted' ? 'text-green' : 'text-ink-mid'}>{describeDraftStanding(draft, isStale)}</dd>
        </>
      )}
    </dl>
  )
}

/**
 * How a worker wants next month to go, as staff read it on the roster row
 * (`GET /workers/{workerId}/shift-preferences`): read-only, since the
 * worker alone sets it; the scheduler builds the schedule from it.
 */
export function ShiftPreferencesPanel({ workerId, period = null }: { workerId: string; /** The month; without it, the month taking preferences. */ period?: string | null }) {
  const query = useShiftPreferences(workerId, period)
  const draftQuery = useDraftSchedule(workerId, period ?? query.data?.period ?? null)
  const resultQuery = useWorkerScheduleResult(workerId, period ?? query.data?.period ?? null)
  const released = resultQuery.data?.result ?? null
  return (
    <section aria-label="Shift preferences" className="p-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <div className="text-[10px] font-bold uppercase tracking-wide text-marine">Shift preferences{query.data && <> · {describePeriod(query.data.period)}</>}</div>
        <EndpointHint>GET /workers/{'{workerId}'}/shift-preferences</EndpointHint>
      </div>
      {query.isPending ? (
        <Skeleton />
      ) : query.isError ? (
        <LoadFailure size="xs" message="The preferences could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <>
          {query.data.limits.bids ? (
            <ShiftPreferencesSummary preferences={query.data.preferences} period={query.data.period} draft={draftQuery.data?.draft ?? null} isStale={draftQuery.data?.isStale ?? false} />
          ) : (
            // A tier the facility assigns has no preferences to give: say what happens instead of "nothing submitted".
            <p className="text-[11px] text-ink">
              <span className="font-bold">{TIER_LABELS[query.data.limits.tier]} workers do not bid here.</span> The month assigns their shifts, up to the tier’s {query.data.limits.typicalHours.min === query.data.limits.typicalHours.max ? `${query.data.limits.typicalHours.max} hours` : `${query.data.limits.typicalHours.min} to ${query.data.limits.typicalHours.max} hours`} a month, so there are no preferences to set.
            </p>
          )}
          {released && (
            <p className="mt-2 text-[11px] text-ink">
              <span className="font-bold text-green">Released:</span> {released.awarded.length} {released.awarded.length === 1 ? 'shift' : 'shifts'} · {released.hours} hrs
              {released.lost.length > 0 && <> · {released.lost.length} asked for went to someone ranked ahead</>}
              {released.flags.includes('under_target') && <span className="text-coral"> · under target</span>}
            </p>
          )}
          {query.data.limits.bids && <p className="mt-2 text-[10px] text-ink-mid/80">The worker sets these in their own app and builds a draft from them; the scheduling system builds the month from the drafts, resolving contests by reliability score.</p>}
        </>
      )}
    </section>
  )
}
